import flatpickr from "flatpickr";
import moment from "moment";
import tippy from "tippy.js";
import "tippy.js/dist/tippy.css";
import { FLATPICKR_CONFIG, FORM_CONTAINER_ID, MOMENT_FORMAT } from "../consts";

/**
 * Serialize all form data into an array of key/value pairs
 * (c) 2020 Chris Ferdinandi, MIT License, https://gomakethings.com
 * @param  {Node}   form The form to serialize
 * @return {Array}       The serialized form data
 */
export const serializeArray = function (form) {
  var arr = [];
  Array.prototype.slice.call(form.elements).forEach(function (field) {
    if (
      !field.name ||
      field.disabled ||
      ["file", "reset", "submit", "button"].indexOf(field.type) > -1
    )
      return;
    if (field.type === "select-multiple") {
      Array.prototype.slice.call(field.options).forEach(function (option) {
        if (!option.selected) return;
        arr.push({
          name: field.name,
          value: option.value,
        });
      });
      return;
    }
    if (["checkbox", "radio"].indexOf(field.type) > -1 && !field.checked)
      return;
    arr.push({
      name: field.name,
      value: field.value,
    });
  });
  return arr;
};

export class FormContainerUtils {
  constructor(
    root,
    userInputs,
    invalidFields,
    createdFormDate,
    initialValues,
    status
  ) {
    this.root = root.querySelector("body");
    this.createdFormDate = createdFormDate;
    this.initialValues = initialValues ?? [];
    this.status = status;
    this.fillUserInput(userInputs);
    this.initFlatpickr();
    this.initTextAreas();
    this.highlightInvalidFields(invalidFields);
    this.initAllTippy();
  }

  /**
   * flatpickr
   */
  initFlatpickr() {
    this.root.querySelectorAll("input[type=date]").forEach((dateInput) => {
      try {
        // set/update min max
        if (dateInput.hasAttribute("data-min-days-from-now")) {
          let newMinDate = moment(this.createdFormDate).add(
            dateInput.getAttribute("data-min-days-from-now"),
            "d"
          );
          if (dateInput.hasAttribute("min")) {
            // min and dynamicStartDate are both set, determine which one will become the actual min value
            newMinDate = moment.max(
              moment(dateInput.getAttribute("min")),
              newMinDate
            );
          }
          dateInput.setAttribute("min", newMinDate.format(MOMENT_FORMAT));
        }

        if (dateInput.hasAttribute("data-max-days-from-now")) {
          let newMaxDate = moment(this.createdFormDate).add(
            dateInput.getAttribute("data-max-days-from-now"),
            "d"
          );
          if (dateInput.hasAttribute("max")) {
            // min and dynamicStartDate are both set, determine which one will become the actual min value
            newMaxDate = moment.max(
              moment(dateInput.getAttribute("max")),
              newMaxDate
            );
          }
          dateInput.setAttribute("max", newMaxDate.format(MOMENT_FORMAT));
        }

        // flatpickr
        if (dateInput.classList.contains("flatpickr")) {
          let dateConfig = {};
          dateConfig.minDate = dateInput.min;
          dateConfig.maxDate = dateInput.max;
          flatpickr(dateInput, { ...FLATPICKR_CONFIG, ...dateConfig });
        }
      } catch (error) {
        console.warn(
          "Das Datumsfeld mit ID " +
            dateInput.id +
            "  konnte nicht korrekt initialisiert werden. Fehler: " +
            error
        );
      }
    });
  }

  /*
   * Validate Fields and return errors
   */
  getFormData(isDraft, validationErrorFn, saveFn) {
    if (!isDraft) {
      let errorMsgs = this.validateFields();
      if (errorMsgs.length > 0) {
        if (validationErrorFn) validationErrorFn(errorMsgs);
        return;
      }
    }

    const form = this.root.querySelector("#" + FORM_CONTAINER_ID);

    const inputData = serializeArray(form)
      .filter((item) => item.name.startsWith("mbf_"))
      .map((item) => {
        let regex = /(\d+)_(\d+)/i;
        if (regex.test(item.name)) {
          item.row = item.name.match(regex)[2];
          item.name = item.name.match(regex)[1];
        }
        item.name = item.name.replace("mbf_", "");
        return item;
      })
      .reduce(function (allInputData, inputEl) {
        const entry = allInputData.find(
          (element) =>
            inputEl.name === element.name && inputEl.row === element.row
        );
        if (entry) {
          if (Array.isArray(entry.value)) entry.value.push(inputEl.value);
          else entry.value = [entry.value, inputEl.value];
        } else {
          allInputData.push(inputEl);
        }
        return allInputData;
      }, []);

    if (saveFn) saveFn(inputData, isDraft);
  }

  /*
   * highlight invalid fields
   */
  highlightInvalidFields(invalidFields) {
    if (!invalidFields) return;
    for (let invalidField of invalidFields) {
      this.addErrorToField(
        "mbf_" + invalidField.name,
        invalidField.errorMessage
      );
    }
  }

  /*
   * local function: add error to field
   */
  addErrorToField(fieldName, msg) {
    if (!fieldName) return;

    let htmlElement = this.root.querySelector(
      "#" + FORM_CONTAINER_ID + ' *[name="' + fieldName + '"]'
    );
    if (htmlElement) {
      htmlElement = htmlElement.parentNode;
      if (
        htmlElement.classList.contains("form-radio") ||
        htmlElement.classList.contains("form-checkbox")
      ) {
        htmlElement = htmlElement.parentNode;
      }
      htmlElement.classList.add("incorrect_field_validation");
      htmlElement.insertAdjacentText("beforeend", msg);
    }
  }

  /*
   * init Tippy
   */
  initAllTippy() {
    for (let el of this.root.querySelectorAll(
      "input[type=text]:read-write, input[type=number]:read-write, textarea:read-write"
    )) {
      this.initTippy(el);
    }
  }

  /*
   * init Tippy
   */
  initTippy(inputEl) {
    if (!inputEl) return;
    tippy(inputEl, {
      placement: "bottom",
      trigger: "input focus click mouseenter",
      onTrigger(instance) {
        if (instance.reference?.validity?.valid) instance.hide();
        instance.setContent(instance.reference?.validationMessage);
      },
      onShow(instance) {
        return !instance.reference?.validity?.valid;
      },
    });
  }

  // returns type of input
  getMBFElementType(element) {
    const firstDocElem = this.root.querySelector('[name="' + element + '"');

    if (firstDocElem !== null) {
      return firstDocElem.type;
    }
    return undefined;
  }

  // returns type of input
  getInitialValue(fieldId) {
    return this.initialValues.find((el) => el.name === fieldId);
  }

  // set value of input
  async setElementValue(name, value, row) {
    const element = row && row > 0 ? name + "_" + row : name;
    let elementType = this.getMBFElementType(element);
    let docElements = this.root.querySelectorAll('[name="' + element + '"');

    try {
      //  set value
      if (value !== null && value !== undefined) {
        if (elementType === "radio") {
          docElements.forEach((el) => {
            el.defaultChecked = el.value === value;
          });
        } else if (elementType === "checkbox") {
          docElements.forEach((el) => {
            // if multipe selected: value stored as array
            el.defaultChecked = Array.isArray(value)
              ? value.indexOf(el.value) !== -1
              : el.value === value;
          });
        } else if (elementType === "hidden") {
          let fp = flatpickr(
            this.root.querySelector("#" + element),
            FLATPICKR_CONFIG
          );

          if (fp) {
            if (Array.isArray(fp)) {
              console.warn(
                "Das Element mit der Id " +
                  element +
                  "  wurde " +
                  fp.length +
                  " mal gefunden."
              );
            } else {
              fp.setDate(value, true, FLATPICKR_CONFIG.dateFormat);
            }
          }
        } else {
          docElements.forEach((el) => (el.value = value));
        }
      }
    } catch (e) {
      console.warn(
        "Fehler beim setzen des Werten " +
          value +
          " (Zeile " +
          row +
          ") in das Element " +
          element +
          ". Fehler: " +
          e.message
      );
    }
  }

  /**
   * Fill User input
   */

  async fillUserInput(userInputs) {
    let tables = [];
    //add row
    const addRow = async (tableId, addInitialValues) => {
      let table = tables.find((x) => x.element.id === tableId);
      let tableIndex = tables.findIndex((x) => x.element.id === tableId);
      if (
        (table && tableIndex !== -1 && table.config.maxNumRows === 0) ||
        table.rows < table.config.maxNumRows
      ) {
        let rowNum = table.rows + 1;
        let newRow = table.config.rowTemplate.replace(
          /(id|for)="mbf_([0-9]+)_([0-9]+)"/gi,
          '$1="mbf_$2_$3_' + rowNum + '"'
        );
        newRow = newRow.replace(
          /(id|name|for)="mbf_([0-9]+)"/gi,
          '$1="mbf_$2_' + rowNum + '"'
        );
        let tableBody = this.root.querySelector("#" + tableId + "_tbody");
        // create element tbody
        if (!tableBody) {
          let tabelEl = this.root.querySelector("#" + tableId);
          if (tabelEl) {
            tableBody = document.createElement("tbody");
            tableBody.id = tableId + "_tbody";
            tabelEl.appendChild(tableBody);
          }
          // set variable
          tableBody = this.root.querySelector("#" + tableId + "_tbody");
        }
        // insert row
        if (tableBody) {
          tableBody.insertAdjacentHTML("beforeend", newRow);
          table.rows = rowNum;
          tables[tableIndex].rows = rowNum;
          this.initFlatpickr();
          this.initAllTippy();
          // fill with initial values if new button pressed
          if (addInitialValues) {
            for (let inputEl of tableBody.lastChild.querySelectorAll(
              "input, select, textarea"
            )) {
              let initialValue = this.getInitialValue(
                inputEl.getAttribute("data-field-id")
              );
              if (initialValue !== null && initialValue !== undefined) {
                // fester wert
                if (!initialValue.hasRows)
                  this.setElementValue(
                    "mbf_" + inputEl.getAttribute("data-field-id"),
                    initialValue.value,
                    rowNum
                  );
              }
            }
          }
        }
      } else if (
        table.config.maxNumRows !== 0 &&
        table.rows >= table.config.maxNumRows
      ) {
        this.root.querySelector("#" + tableId + "_add_row").hidden = true;
      }
    };

    // set value of input
    const setUserInputs = async (name, value, row) => {
      const element = row && row > 0 ? name + "_" + row : name;
      let docElements = this.root.querySelectorAll('[name="' + element + '"');
      // row missing:  add row to table-body
      if (row > 0 && docElements.length === 0) {
        if (tables.length > 0) {
          for (let searchTable of tables) {
            if (searchTable.columns.includes(name) && searchTable.element) {
              let headerIndex = searchTable.element.querySelector("thead")
                ? 0
                : 1;
              while (
                !searchTable.element.querySelectorAll("tbody tr") ||
                searchTable.element.querySelectorAll("tbody tr").length <
                  row + headerIndex
              ) {
                await addRow(
                  searchTable.element.id,
                  this.status === "open" || this.status === "read"
                );
              }
              break;
            }
          }
        }
      }
      this.setElementValue(name, value, row);
    };

    const init = async () => {
      // get tables from dom
      for (const dynamicTable of this.root.querySelectorAll(".mbf_dt")) {
        // parse config
        let config = JSON.parse(
          window.atob(dynamicTable.getAttribute("data-config"))
        );
        // get input names from config
        let childFields = document.createElement("div");
        childFields.innerHTML = config?.rowTemplate;
        let table = {
          element: dynamicTable,
          config,
          rows: 0,
          columns: [],
        };
        for (let tableColumn of childFields.querySelectorAll(
          "input, textarea"
        )) {
          table.columns.push(tableColumn.name);
        }
        tables.push(table);
        // add  row-btn addEventListener
        if (config.initNumRows < config.maxNumRows || config.maxNumRows === 0) {
          const addRowButton = this.root.querySelector(
            "#" + dynamicTable.id + "_add_row"
          );
          if (addRowButton)
            addRowButton.addEventListener("click", function () {
              addRow(dynamicTable.id, true);
            });
        }
      }

      if (!(userInputs === null || userInputs === undefined)) {
        // fill fields
        userInputs.map((field) => {
          setUserInputs(
            "mbf_" + field.name,
            field.value,
            field.row ? parseInt(field.row) : field.row
          );
        });
      }
    };

    const addInitRows = async () => {
      for (let table of tables) {
        //table header is in t_body
        if (Number.isInteger(table.config?.initNumRows)) {
          let countRow = this.root.querySelectorAll(
            "#" + table.element.id + " tbody tr"
          ).length;
          while (countRow < table.config?.initNumRows) {
            countRow++;
            await addRow(table.element.id, true);
          }
        }
      }
    };

    await init();
    addInitRows();
  }

  /**
   * Add some functionalities to textareas
   */
  initTextAreas() {
    const addMaxLengthCounter = (elem) => {
      if (elem.maxLength < 0) {
        return;
      }

      const charsLeft = elem.maxLength - elem.value.length,
        counterText = `${elem.value.length}/${elem.maxLength}`,
        counterElement = this.root.querySelector(
          "#" + elem.id + "_charcounter"
        );

      if (counterElement) {
        counterElement.innerText = counterText;

        // show counter when only 20% of max character are left
        charsLeft > (elem.maxLength * 20) / 100
          ? counterElement.classList.add("hidden")
          : counterElement.classList.remove("hidden");

        // add warning color only 10% of max character are left
        charsLeft <= (elem.maxLength * 10) / 100 && charsLeft !== 0
          ? counterElement.classList.add("warning")
          : counterElement.classList.remove("warning");

        // no characters left
        charsLeft === 0
          ? counterElement.classList.add("error")
          : counterElement.classList.remove("error");
        // show counter when only 20% of max character are left
      } else if (charsLeft <= (elem.maxLength * 20) / 100) {
        elem.parentNode.insertAdjacentHTML(
          "afterEnd",
          `<div id="${elem.id}_charcounter" class="charcounter">${counterText}</div>`
        );
      }
    };

    const autosize = (elem) => {
      elem.style.height = elem.scrollHeight - 2 + "px";
      if (elem.parentNode.parentNode.offsetParent.tagName === "TD") {
        elem.parentNode.parentNode.parentNode.style.height =
          elem.scrollHeight - 2 + "px;";
      }
    };

    for (let textareaEl of this.root.querySelectorAll(
      "#" + FORM_CONTAINER_ID + " textarea"
    )) {
      // resize all visible textareas at start
      autosize(textareaEl);
      addMaxLengthCounter(textareaEl);
      textareaEl.addEventListener("input", function () {
        addMaxLengthCounter(this);
        autosize(this);
      });
    }
  }

  /**
   * Validate
   */
  validateFields() {
    let validations = [];
    let filledOptions = [];

    const addValidationError = (fieldName, msg, label) => {
      validations.push({
        label: label,
        msg: msg,
      });
      this.addErrorToField(fieldName, msg);
    };

    // all inputs
    for (let field of this.root.querySelectorAll(
      "#" + FORM_CONTAINER_ID + " input, #" + FORM_CONTAINER_ID + " textarea"
    )) {
      if (field.type === "checkbox" || field.type === "radio") {
        if (field.checked) {
          filledOptions.push(field.name);
        }
      } else {
        if (field.validationMessage) {
          let label = this.root.querySelector('label[for="' + field.name + '"');
          label = label ? label.innerText : "Kein Label (" + field.type + ")";
          if (addValidationError(field.name, field.validationMessage, label));
        }
      }
    }

    // special case: options
    for (let field of this.root.querySelectorAll(
      "#" +
        FORM_CONTAINER_ID +
        ' input[type="checkbox"]:required, #' +
        FORM_CONTAINER_ID +
        ' input[type="radio"]:required'
    )) {
      if (filledOptions.indexOf(field.name) === -1) {
        addValidationError(
          field.name,
          field.type === "checkbox"
            ? "Wählen Sie mindestens eine der Optionen aus."
            : "Wählen Sie eine Option aus.",
          field.type === "checkbox" ? "Mehrfachauswahl" : "Einfachauswahl"
        );
        filledOptions.push(field.name);
      }
    }
    return validations;
  }
}
